import { useEffect, useState } from "react";
import api from "@/lib/api";
import CandleChart from "@/components/CandleChart";
import { toast } from "sonner";
import { Play, Pause, RefreshCw, AlertTriangle, Timer, Wallet, TrendingUp, Wrench, ShieldOff } from "lucide-react";

const POLL_MS = 4000;

function formatElapsed(startIso) {
  if (!startIso) return "—";
  const start = new Date(startIso).getTime();
  if (Number.isNaN(start)) return "—";
  let s = Math.max(0, Math.floor((Date.now() - start) / 1000));
  const d = Math.floor(s / 86400); s -= d * 86400;
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); s -= m * 60;
  const pad = (n) => String(n).padStart(2, "0");
  return d > 0 ? `${d}d ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function Stat({ label, value, sub, tone = "default", testId }) {
  const toneCls =
    tone === "buy" ? "text-teal-400"
      : tone === "sell" ? "text-rose-400"
      : tone === "warn" ? "text-amber-400"
      : "text-zinc-100";
  return (
    <div className="border border-white/10 bg-[#121214] rounded-sm p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-2xl font-mono mt-1 tabular-nums ${toneCls}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1 font-mono">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [brokers, setBrokers] = useState(null);
  const [pair, setPair] = useState("XAU_USD");
  const [candles, setCandles] = useState([]);
  const [signals, setSignals] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [actLoading, setActLoading] = useState(false);

  const load = async () => {
    try {
      const [s, st, b, sigs, tr] = await Promise.all([
        api.get("/bot/settings"), api.get("/bot/status"),
        api.get("/brokers/status"), api.get("/signals?limit=5"),
        api.get("/trades?limit=10"),
      ]);
      setSettings(s.data); setStatus(st.data); setBrokers(b.data);
      setSignals(sigs.data); setTrades(tr.data);
    } catch (e) {
      // ignore — surfaced elsewhere
    }
  };

  const loadChart = async (p) => {
    if (!p) return;
    setLoadingChart(true);
    try {
      const { data } = await api.get(`/market/candles?pair=${p}&count=40`);
      setCandles(data.candles);
    } catch (e) {
      setCandles([]);
    } finally { setLoadingChart(false); }
  };

  useEffect(() => { load(); const i = setInterval(load, POLL_MS); return () => clearInterval(i); }, []);
  useEffect(() => { loadChart(pair); const i = setInterval(() => loadChart(pair), 30000); return () => clearInterval(i); }, [pair]);

  // Tick once per second so the Running Timer updates live without re-fetching status
  const [, setTick] = useState(0);
  useEffect(() => { const i = setInterval(() => setTick((n) => n + 1), 1000); return () => clearInterval(i); }, []);

  const lastSignal = signals[0];
  const openTrades = trades.filter((t) => t.status === "open");

  const startBot = async () => {
    setActLoading(true);
    try { await api.post("/bot/start"); toast.success("Bot started"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setActLoading(false); }
  };
  const stopBot = async () => {
    setActLoading(true);
    try {
      const { data } = await api.post("/bot/stop");
      if (data?.reset_starting_balance && data?.new_starting_balance) {
        toast.success(`Bot stopped · starting balance rebased to $${data.new_starting_balance.toFixed(2)}`);
      } else {
        toast.success("Bot stopped");
      }
      await load();
    }
    catch { toast.error("Failed"); }
    finally { setActLoading(false); }
  };

  const flush = async () => {
    if (!window.confirm("Maintenance flush\n\nClears stuck connection state, error banners, and the MetaApi cache. Your broker stays connected. Page will refresh.\n\nContinue?")) return;
    setActLoading(true);
    try {
      await api.post("/maintenance/flush");
      toast.success("State flushed — refreshing…");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Flush failed");
      setActLoading(false);
    }
  };

  const overrideCooldown = async () => {
    if (!window.confirm("Override the active session cool-down and re-enable Start Bot immediately?\n\nUse ONLY if you understand you're going past today's SL/TP guard. Prop-firm rules usually forbid trading past a hit limit."))
      return;
    setActLoading(true);
    try {
      const { data } = await api.post("/bot/cooldown/reset");
      if (data?.was_in_cooldown) {
        toast.success("Cool-down cleared — press Start Bot to resume");
      } else {
        toast.success("No active cool-down to clear");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to override cool-down");
    } finally {
      setActLoading(false);
    }
  };

  const cooldownActive = status?.bot_cooldown_until
    && new Date(status.bot_cooldown_until) > new Date();

  const setMode = async (mode) => {
    if (!settings) return;
    const next = { ...settings, mode };
    await api.put("/bot/settings", next);
    setSettings(next);
    toast.success(`Mode → ${mode.toUpperCase()}`);
    loadChart(pair);
  };

  const manualTrade = async (side) => {
    try {
      const { data } = await api.post("/trades/manual", {
        pair, side, lot_size: settings?.lot_size || 0.01,
        broker: "mt5",
      });
      toast.success(`${side} ${pair} sent · ${data.trade_id?.slice(0, 8)}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trade failed");
    }
  };

  const closeTrade = async (tid) => {
    try { await api.post(`/trades/${tid}/close`); toast.success("Trade closed"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Close failed"); }
  };

  const closeAll = async () => {
    if (!openTrades.length) return;
    if (!window.confirm(`Close all ${openTrades.length} open positions? This will unblock the bot.`)) return;
    setActLoading(true);
    let okCount = 0, errCount = 0;
    for (const t of openTrades) {
      try { await api.post(`/trades/${t.id}/close`); okCount += 1; }
      catch { errCount += 1; }
    }
    toast.success(`Closed ${okCount}${errCount ? ` · ${errCount} failed` : ""}`);
    await load();
    setActLoading(false);
  };

  const enabled = status?.enabled;
  const mt5Ok = brokers?.mt5_connected;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="dashboard">
      {/* Top: Title + bot controls */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Control Room</div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1">
            <h1 className="text-xl sm:text-2xl font-medium tracking-tight">Live Engine</h1>
            <span
              className={`text-[10px] font-mono uppercase border px-2 py-0.5 ${
                enabled
                  ? "border-teal-400/30 text-teal-400 bg-teal-400/5"
                  : "border-zinc-700 text-zinc-500"
              }`}
              data-testid="bot-status-badge"
            >
              {enabled ? "● Running" : "○ Idle"}
            </span>
            <span
              className={`text-[10px] font-mono uppercase border px-2 py-0.5 ${
                settings?.mode === "live"
                  ? "border-rose-400/30 text-rose-400 bg-rose-400/5"
                  : "border-amber-400/30 text-amber-400 bg-amber-400/5"
              }`}
              data-testid="mode-badge"
            >
              {settings?.mode === "live" ? "LIVE" : "DEMO"}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex border border-white/10 rounded-sm overflow-hidden">
            <button data-testid="mode-demo-button" onClick={() => setMode("demo")}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest ${
                      settings?.mode === "demo" ? "bg-amber-400/10 text-amber-400" : "text-zinc-500"}`}>
              Demo
            </button>
            <button data-testid="mode-live-button" onClick={() => setMode("live")}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border-l border-white/10 ${
                      settings?.mode === "live" ? "bg-rose-400/10 text-rose-400" : "text-zinc-500"}`}>
              Live
            </button>
          </div>

          {!enabled ? (
            <button data-testid="start-bot-button" onClick={startBot} disabled={actLoading || !mt5Ok}
                    className="flex items-center gap-2 bg-teal-400/10 text-teal-400 border border-teal-400/30 hover:bg-teal-400/20 px-3 py-1.5 text-xs font-mono uppercase tracking-widest disabled:opacity-50">
              <Play className="w-3 h-3" /> Start Bot
            </button>
          ) : (
            <button data-testid="stop-bot-button" onClick={stopBot} disabled={actLoading}
                    className="flex items-center gap-2 bg-rose-400/10 text-rose-400 border border-rose-400/30 hover:bg-rose-400/20 px-3 py-1.5 text-xs font-mono uppercase tracking-widest">
              <Pause className="w-3 h-3" /> Stop Bot
            </button>
          )}
          <button data-testid="flush-button" onClick={flush} disabled={actLoading}
                  title="Maintenance flush — clears stuck connection/error state and refreshes"
                  className="flex items-center gap-2 bg-amber-400/10 text-amber-400 border border-amber-400/30 hover:bg-amber-400/20 px-3 py-1.5 text-xs font-mono uppercase tracking-widest disabled:opacity-50">
            <Wrench className="w-3 h-3" /> Flush
          </button>
          <button data-testid="cooldown-override-button" onClick={overrideCooldown} disabled={actLoading || !cooldownActive}
                  title={cooldownActive
                    ? `Force-clear the active session cool-down. Bot ends at ${new Date(status.bot_cooldown_until).toLocaleString(undefined, {hour: "2-digit", minute: "2-digit", month: "short", day: "numeric"})} — this overrides it now.`
                    : "No active session cool-down to override"}
                  className={`flex items-center gap-2 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border ${
                    cooldownActive
                      ? "bg-rose-400/10 text-rose-400 border-rose-400/40 hover:bg-rose-400/20"
                      : "bg-zinc-900 text-zinc-600 border-zinc-800 cursor-not-allowed"
                  } disabled:opacity-60`}>
            <ShieldOff className="w-3 h-3" /> Override Cool-down
          </button>
        </div>
      </div>

      {/* Connection warning */}
      {!mt5Ok && (
        <div className="border border-amber-400/30 bg-amber-400/5 text-amber-300 text-sm font-mono p-3 flex items-start gap-2 rounded-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>Connect your MT5 broker to start trading. Visit Brokers.</div>
        </div>
      )}

      {/* Auto-stop banner — bot halted by risk control */}
      {status?.auto_stop_reason && (
        <div className={`border rounded-sm p-4 flex items-start gap-3 ${
          status.auto_stop_reason === "tp_hit"
            ? "border-teal-400/30 bg-teal-400/5 text-teal-300"
            : "border-rose-400/30 bg-rose-400/5 text-rose-300"
        }`} data-testid="auto-stop-banner">
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1 font-mono text-sm leading-relaxed">
            <div className="uppercase tracking-widest text-[10px] mb-1">
              {status.auto_stop_reason === "tp_hit" ? "Take-profit target reached" : "Max drawdown hit"}
            </div>
            <div>
              Bot auto-stopped at <span className="font-medium">{status.auto_stop_change_pct >= 0 ? "+" : ""}{status.auto_stop_change_pct}%</span> change vs starting balance ({new Date(status.auto_stop_at).toLocaleString()}). Press <span className="font-medium">Start Bot</span> to resume — adjust risk thresholds in <a className="underline" href="/settings">Settings</a>.
            </div>
          </div>
        </div>
      )}

      {/* Last tick error — auto-trading is silently skipping. Cleared on next successful tick. */}
      {status?.enabled && status?.last_tick_error && (
        <div className="border border-amber-400/30 bg-amber-400/5 text-amber-200 rounded-sm p-3 flex items-start gap-2 font-mono text-sm"
             data-testid="tick-error-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="uppercase tracking-widest text-[10px] mb-1">Auto-trade error · last tick</div>
            <div className="break-words text-xs">{status.last_tick_error}</div>
            <div className="text-[10px] text-amber-400/60 mt-1">
              {status.last_tick_error_at ? new Date(status.last_tick_error_at).toLocaleString() : ""} — clears automatically on the next successful tick.
            </div>
          </div>
        </div>
      )}

      {/* Floating PnL banner — only when active trades */}
      {status?.open_positions > 0 && (
        <div
          className={`border rounded-sm p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 ${
            (status?.open_floating_pnl ?? 0) >= 0
              ? "border-teal-400/30 bg-teal-400/5"
              : "border-rose-400/30 bg-rose-400/5"
          }`}
          data-testid="floating-pnl-banner"
        >
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-400">
              Live Floating P&amp;L · {status.open_positions} active position{status.open_positions > 1 ? "s" : ""}
            </div>
            <div
              className={`text-4xl sm:text-5xl lg:text-6xl font-mono mt-1 tabular-nums font-medium leading-none break-all ${
                (status?.open_floating_pnl ?? 0) >= 0 ? "text-teal-400" : "text-rose-400"
              }`}
              data-testid="floating-pnl-value"
            >
              {(status?.open_floating_pnl ?? 0) >= 0 ? "+" : ""}
              {(status?.open_floating_pnl ?? 0).toFixed(2)}
              <span className="text-xl sm:text-2xl text-zinc-500 ml-2">
                {brokers?.mt5_currency || "USD"}
              </span>
            </div>
          </div>
          <div className="text-left sm:text-right text-xs font-mono text-zinc-400 grid grid-cols-2 sm:grid-cols-1 gap-x-4 gap-y-1">
            <div>Equity: <span className="text-zinc-100 tabular-nums">{(brokers?.mt5_equity ?? 0).toFixed(2)}</span></div>
            <div>Balance: <span className="text-zinc-100 tabular-nums">{(brokers?.mt5_balance ?? 0).toFixed(2)}</span></div>
          </div>
        </div>
      )}

      {/* Hero: Running Timer · Starting Balance · Overall PnL */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="hero-metrics-row">
        <div className="border border-white/10 bg-[#121214] rounded-sm p-4 sm:p-5" data-testid="hero-runtime">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
            <Timer className="w-3.5 h-3.5 text-teal-400" strokeWidth={1.5} /> Bot Runtime
          </div>
          <div className="text-3xl sm:text-4xl font-mono mt-2 tabular-nums text-zinc-100" data-testid="hero-runtime-value">
            {formatElapsed(status?.bot_started_at)}
          </div>
          <div className="text-[10px] text-zinc-600 mt-1 font-mono">
            {status?.bot_started_at ? `Since ${new Date(status.bot_started_at).toLocaleString()}` : "Start the bot to begin counting"}
          </div>
        </div>

        <div className="border border-white/10 bg-[#121214] rounded-sm p-4 sm:p-5" data-testid="hero-starting-balance">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
            <Wallet className="w-3.5 h-3.5 text-teal-400" strokeWidth={1.5} /> Starting Balance
          </div>
          <div className="text-3xl sm:text-4xl font-mono mt-2 tabular-nums text-zinc-100" data-testid="hero-starting-balance-value">
            {status?.starting_balance != null
              ? `${status.starting_balance.toFixed(2)}`
              : "—"}
            {status?.starting_balance != null && (
              <span className="text-base text-zinc-500 ml-2">{brokers?.mt5_currency || "USD"}</span>
            )}
          </div>
          <div className="text-[10px] text-zinc-600 mt-1 font-mono">
            {status?.starting_balance_at
              ? `Snapshot ${new Date(status.starting_balance_at).toLocaleDateString()}`
              : "Captured on bot start"}
          </div>
        </div>

        <div className={`border rounded-sm p-4 sm:p-5 ${
          (status?.overall_pnl ?? 0) >= 0 ? "border-teal-400/20 bg-teal-400/[0.03]" : "border-rose-400/20 bg-rose-400/[0.03]"
        }`} data-testid="hero-overall-pnl">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
            <TrendingUp className="w-3.5 h-3.5 text-teal-400" strokeWidth={1.5} /> Overall PnL
          </div>
          <div className={`text-3xl sm:text-4xl font-mono mt-2 tabular-nums ${
            (status?.overall_pnl ?? 0) >= 0 ? "text-teal-400" : "text-rose-400"
          }`} data-testid="hero-overall-pnl-value">
            {(status?.overall_pnl ?? 0) >= 0 ? "+" : ""}{(status?.overall_pnl ?? 0).toFixed(2)}
            <span className="text-base text-zinc-500 ml-2">{brokers?.mt5_currency || "USD"}</span>
          </div>
          <div className="text-[10px] text-zinc-600 mt-1 font-mono">
            {status?.overall_trades ?? 0} closed trade{(status?.overall_trades ?? 0) === 1 ? "" : "s"} since snapshot
          </div>
        </div>
      </div>

      {/* Session risk thermometer + cooldown banner */}
      <SessionRiskMeter status={status} currency={brokers?.mt5_currency || "USD"} />

      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Balance" value={
          mt5Ok && brokers?.mt5_balance != null
            ? `${brokers.mt5_balance.toFixed(2)} ${brokers.mt5_currency || ""}`
            : "—"
        } sub={mt5Ok ? "MT5 acct" : "Not connected"} testId="stat-balance" />
        <Stat label="PnL · 24h" value={
          status?.pnl_24h != null
            ? `${status.pnl_24h >= 0 ? "+" : ""}${status.pnl_24h.toFixed(2)}`
            : "—"
        } sub={`${status?.pnl_24h_trades ?? 0} closed · W ${status?.pnl_24h_wins ?? 0} / L ${status?.pnl_24h_losses ?? 0}`}
              tone={(status?.pnl_24h ?? 0) >= 0 ? "buy" : "sell"} testId="stat-pnl-24h" />
        <Stat label="Open Positions" value={status?.open_positions ?? 0}
              sub={status?.open_floating_pnl ? `Float: ${status.open_floating_pnl >= 0 ? "+" : ""}${status.open_floating_pnl.toFixed(2)}` : null}
              testId="stat-open"
              tone={(status?.open_floating_pnl ?? 0) >= 0 ? "buy" : "sell"} />
        <Stat label="Signals Today" value={status?.today_signals ?? 0} testId="stat-signals" />
        <Stat label="Trades Today" value={status?.today_trades ?? 0} testId="stat-trades" />
      </div>

      {/* Chart + side panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-white/10 bg-[#121214] rounded-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 sm:px-4 py-3">
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Chart</div>
              <select data-testid="chart-pair-select" value={pair} onChange={(e) => setPair(e.target.value)}
                      className="bg-black border border-white/10 text-xs font-mono px-2 py-1 rounded-sm focus:outline-none">
                {(settings?.pairs || ["XAU_USD", "USD_JPY"]).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">M5</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <button onClick={() => loadChart(pair)} data-testid="chart-refresh-button"
                      className="text-zinc-500 hover:text-zinc-200 p-1" title="Refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${loadingChart ? "animate-spin" : ""}`} />
              </button>
              <button data-testid="manual-buy-button" onClick={() => manualTrade("BUY")}
                      className="bg-teal-400/10 text-teal-400 border border-teal-400/30 hover:bg-teal-400/20 px-2 py-1 text-[11px] font-mono uppercase tracking-widest">
                Buy {settings?.lot_size}
              </button>
              <button data-testid="manual-sell-button" onClick={() => manualTrade("SELL")}
                      className="bg-rose-400/10 text-rose-400 border border-rose-400/30 hover:bg-rose-400/20 px-2 py-1 text-[11px] font-mono uppercase tracking-widest">
                Sell {settings?.lot_size}
              </button>
            </div>
          </div>
          <div className="p-2">
            <CandleChart candles={candles} signal={lastSignal && lastSignal.pair === pair ? lastSignal : null} />
          </div>
        </div>

        <div className="border border-white/10 bg-[#121214] rounded-sm">
          <div className="border-b border-white/10 px-4 py-3 text-[10px] uppercase tracking-widest text-zinc-500">
            Last Signal
          </div>
          <div className="p-4">
            {lastSignal ? (
              <div className="space-y-2 font-mono text-sm" data-testid="last-signal">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Side</span>
                  <span className={lastSignal.side === "BUY" ? "text-teal-400" : "text-rose-400"}>{lastSignal.side}</span>
                </div>
                <div className="flex justify-between"><span className="text-zinc-500">Pair</span><span>{lastSignal.pair}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Entry</span><span className="tabular-nums">{lastSignal.entry_price?.toFixed(5)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">SL</span><span className="tabular-nums">{lastSignal.stop_loss?.toFixed(5)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">TP</span><span className="tabular-nums">{lastSignal.take_profit?.toFixed(5)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Executions</span><span>{lastSignal.executions || 0}</span></div>
                <div className="text-[10px] text-zinc-600 mt-2">{lastSignal.detected_at}</div>
              </div>
            ) : (
              <div className="text-sm text-zinc-500">No signals yet. Start the bot and wait for the next 5-min pattern.</div>
            )}
          </div>
        </div>
      </div>

      {/* Open positions */}
      <div className="border border-white/10 bg-[#121214] rounded-sm">
        <div className="border-b border-white/10 px-3 sm:px-4 py-3 text-[10px] uppercase tracking-widest text-zinc-500 flex justify-between items-center">
          <span>Open Positions · <span data-testid="open-positions-count">{openTrades.length}</span></span>
          {openTrades.length > 0 && (
            <button data-testid="close-all-button" onClick={closeAll} disabled={actLoading}
                    className="bg-rose-400/10 text-rose-400 border border-rose-400/30 hover:bg-rose-400/20 px-2 py-1 text-[10px] font-mono uppercase tracking-widest disabled:opacity-50">
              Close All
            </button>
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <th className="text-left py-2 px-4">Time</th>
                <th className="text-left">Pair</th>
                <th className="text-left">Side</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Entry</th>
                <th className="text-right">SL</th>
                <th className="text-right">TP</th>
                <th className="text-left">Broker</th>
                <th className="text-right pr-4">Action</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {openTrades.length === 0 && (
                <tr><td colSpan={9} className="text-center text-zinc-500 py-6">No open positions</td></tr>
              )}
              {openTrades.map((t) => {
                const isFutures = t.broker === "topstep";
                const priceDp = isFutures ? 2 : 5;
                return (
                <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`open-row-${t.id}`}>
                  <td className="py-2 px-4 text-xs text-zinc-400">{t.opened_at?.slice(11, 19)}</td>
                  <td>{t.pair}</td>
                  <td className={t.side === "BUY" ? "text-teal-400" : "text-rose-400"}>{t.side}</td>
                  <td className="text-right tabular-nums">{t.lot_size}{isFutures ? "×" : ""}</td>
                  <td className="text-right tabular-nums">{t.entry_price?.toFixed(priceDp) || "—"}</td>
                  <td className="text-right tabular-nums">{t.stop_loss?.toFixed(priceDp) || "—"}</td>
                  <td className="text-right tabular-nums">{t.take_profit?.toFixed(priceDp) || "—"}</td>
                  <td className="text-xs uppercase text-zinc-400">{isFutures ? "TopStep" : t.broker}</td>
                  <td className="text-right pr-4">
                    <button data-testid={`close-trade-${t.id}`} onClick={() => closeTrade(t.id)}
                            className="text-xs uppercase tracking-widest text-rose-400 hover:text-rose-300">
                      Close
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden divide-y divide-white/5">
          {openTrades.length === 0 && (
            <div className="text-center text-zinc-500 py-6 text-sm">No open positions</div>
          )}
          {openTrades.map((t) => {
            const isFutures = t.broker === "topstep";
            const priceDp = isFutures ? 2 : 5;
            return (
            <div key={t.id} className="p-3 font-mono" data-testid={`open-row-${t.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium ${t.side === "BUY" ? "text-teal-400" : "text-rose-400"}`}>
                    {t.side}
                  </span>
                  <span className="text-sm text-zinc-100">{t.pair}</span>
                  <span className="text-[10px] text-zinc-500 uppercase">{isFutures ? "TopStep" : t.broker}</span>
                </div>
                <button data-testid={`close-trade-${t.id}`} onClick={() => closeTrade(t.id)}
                        className="text-[10px] uppercase tracking-widest text-rose-400 hover:text-rose-300 border border-rose-400/30 hover:bg-rose-400/10 px-2 py-1 rounded-sm">
                  Close
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2 text-[11px]">
                <div>
                  <div className="text-[9px] uppercase text-zinc-600 tracking-widest">{isFutures ? "Contracts" : "Lot"}</div>
                  <div className="text-zinc-200 tabular-nums">{t.lot_size}{isFutures ? "×" : ""}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-zinc-600 tracking-widest">Entry</div>
                  <div className="text-zinc-200 tabular-nums">{t.entry_price?.toFixed(priceDp) || "—"}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-zinc-600 tracking-widest">SL</div>
                  <div className="text-rose-300 tabular-nums">{t.stop_loss?.toFixed(priceDp) || "—"}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase text-zinc-600 tracking-widest">TP</div>
                  <div className="text-teal-300 tabular-nums">{t.take_profit?.toFixed(priceDp) || "—"}</div>
                </div>
              </div>
              <div className="text-[10px] text-zinc-600 mt-2">{t.opened_at?.slice(11, 19)} UTC</div>
            </div>
          );})}
        </div>
      </div>
    </div>
  );
}

function SessionRiskMeter({ status, currency }) {
  const tp = status?.tp_target_dollars;
  const sl = status?.sl_risk_dollars;
  const pnl = status?.session_pnl_dollars ?? 0;
  const pnlPct = status?.session_pnl_pct;
  const equity = status?.current_equity;
  const start = status?.starting_balance;
  const cooldown = status?.bot_cooldown_until;
  const inCooldown = cooldown && new Date(cooldown) > new Date();

  // Show nothing if no targets are configured and no cooldown active
  if (!tp && !sl && !inCooldown) return null;

  const tpPct = Math.min(100, Math.round((status?.tp_progress ?? 0) * 100));
  const slPct = Math.min(100, Math.round((status?.sl_progress ?? 0) * 100));
  const pnlColor = pnl >= 0 ? "text-teal-400" : "text-rose-400";
  const pnlSign = pnl >= 0 ? "+" : "";

  return (
    <div className="border border-white/10 bg-[#121214] rounded-sm p-4 sm:p-5 space-y-4" data-testid="session-risk-meter">
      {inCooldown && (
        <div className="flex items-center gap-2 border border-amber-400/30 bg-amber-400/5 rounded-sm px-3 py-2" data-testid="cooldown-banner">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
          <div className="text-xs text-amber-300 leading-relaxed">
            <span className="font-mono uppercase tracking-widest text-[10px]">Session cool-down</span>
            <span className="text-amber-200/80 ml-2 font-mono">
              Bot resumes at 5:00 PM EST · {new Date(cooldown).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}
            </span>
            <span className="text-amber-200/60 ml-2 font-mono text-[10px]">
              (Use "Override Cool-down" in the header to force resume.)
            </span>
          </div>
        </div>
      )}

      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Session PnL</div>
          <div className={`text-2xl sm:text-3xl font-mono tabular-nums ${pnlColor}`} data-testid="session-pnl-value">
            {pnlSign}{pnl.toFixed(2)} <span className="text-sm text-zinc-500">{currency}</span>
            {pnlPct != null && (
              <span className="text-sm text-zinc-500 ml-2">({pnlSign}{pnlPct.toFixed(2)}%)</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Equity · Starting</div>
          <div className="text-sm font-mono tabular-nums text-zinc-300">
            {equity != null ? equity.toFixed(2) : "—"}
            <span className="text-zinc-600 mx-1">/</span>
            {start != null ? start.toFixed(2) : "—"}
          </div>
        </div>
      </div>

      {/* Thermometer: [ red-fill ][ 0 ][ green-fill ] */}
      <div className="space-y-2" data-testid="thermometer">
        <div className="flex items-center gap-1">
          {/* Left half — SL / risk */}
          <div className="flex-1 relative h-6 bg-black border border-white/10 rounded-l-sm overflow-hidden">
            <div
              data-testid="thermometer-sl-fill"
              className="absolute top-0 right-0 h-full bg-gradient-to-l from-rose-500 to-rose-500/50 transition-all duration-500"
              style={{ width: `${slPct}%` }}
            />
            <div className="absolute inset-0 flex items-center justify-start px-2 text-[10px] font-mono text-zinc-400 uppercase tracking-widest">
              Risk {sl ? `−$${sl.toFixed(0)}` : "—"}
            </div>
          </div>
          {/* Center marker */}
          <div className="w-1 h-8 bg-zinc-500 rounded-full" />
          {/* Right half — TP / target */}
          <div className="flex-1 relative h-6 bg-black border border-white/10 rounded-r-sm overflow-hidden">
            <div
              data-testid="thermometer-tp-fill"
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-teal-500/50 to-teal-500 transition-all duration-500"
              style={{ width: `${tpPct}%` }}
            />
            <div className="absolute inset-0 flex items-center justify-end px-2 text-[10px] font-mono text-zinc-400 uppercase tracking-widest">
              Target {tp ? `+$${tp.toFixed(0)}` : "—"}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between text-[10px] font-mono text-zinc-600">
          <span data-testid="thermometer-sl-pct">{slPct}% risked</span>
          <span className="text-zinc-500">↕ session breakeven ↕</span>
          <span data-testid="thermometer-tp-pct">{tpPct}% toward target</span>
        </div>
      </div>
    </div>
  );
}

